import { yupResolver } from '@hookform/resolvers/yup';
import SearchIcon from '@mui/icons-material/Search';
import { useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useTheme, styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import PropTypes from 'prop-types';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { object, string, bool } from 'yup';

import Text from '../../elements/text/Text';

const schema = object({
  query: string()
    .trim()
    .required()
    .test('length', 'conditional length', (value, ctx) => {
      if (ctx.parent.isExact) return value.length > 0;
      return value.length > 1;
    }),
  isExact: bool().required(),
});

const StyledRoot = styled('form')({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
});
const StyledButton = styled(Button)({
  borderTopLeftRadius: 0,
  borderBottomLeftRadius: 0,
});

const StyledInput = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },
});

const SearchBar = ({
  isSearching,
  numberFound,
  searchConfig,
  updateSearchConfig,
}) => {
  const { t } = useTranslation();

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { isValid },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      query: '',
      isExact: true,
    },
  });

  const isExact = watch('isExact');

  const onSubmit = async (data) => {
    updateSearchConfig(data);
  };

  const clearSearch = () => {
    setValue('query', '');
    updateSearchConfig({
      query: '',
      isExact: true,
    });
  };

  return (
    <StyledRoot onSubmit={handleSubmit(onSubmit)}>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex' }}>
          <Controller
            name="query"
            control={control}
            render={({ field }) => (
              <StyledInput
                {...field}
                fullWidth
                variant="outlined"
                size="small"
                inputProps={{
                  'aria-label': 'search bar',
                  style: { paddingLeft: 10 },
                }}
                InputProps={{
                  startAdornment: <SearchIcon />,
                }}
                autoComplete="off"
                placeholder={t('synonym.searchWord', { ns: 'project' })}
                onChange={(e) =>
                  setValue('query', e.target.value, { shouldValidate: true })
                }
              />
            )}
          />

          <StyledButton
            type="submit"
            aria-label="search"
            variant="outlined"
            disabled={!isValid}
          >
            {t('search', { ns: 'buttons' })}
          </StyledButton>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'spac-between',
          alignItems: 'center',
          flexDirection: isSmallScreen ? 'column' : 'row',
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={isExact}
              inputProps={{ 'aria-label': 'search with exact match' }}
              onChange={(e) =>
                setValue('isExact', e.target.checked, {
                  shouldValidate: true,
                })
              }
              name="isExact"
            />
          }
          label={t('synonym.exactSearch', { ns: 'project' })}
        />
        {isSearching ? (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Text caption style={{ marginRight: 10 }}>
              {t('synonym.totalHit', {
                ns: 'project',
                word: searchConfig.query,
                number: numberFound.toLocaleString(),
              })}
            </Text>
            <Button size="small" variant="text" onClick={() => clearSearch()}>
              {t('synonym.clearSearch', { ns: 'project' })}
            </Button>
          </Box>
        ) : (
          <Text caption style={{ marginRight: 10 }}>
            {t('synonym.totalWords', {
              ns: 'project',
              number: numberFound.toLocaleString(),
            })}
          </Text>
        )}
      </Box>
    </StyledRoot>
  );
};

SearchBar.propTypes = {
  isSearching: PropTypes.bool,
  numberFound: PropTypes.number,
  searchConfig: PropTypes.object,
  updateSearchConfig: PropTypes.func,
};

export default SearchBar;
